import React from 'react';
import { LeftOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import OrbitMarkReply from '../../../components/OrbitMarkReply';
import { TOrbit } from '../../../i18n/TModules';
import { LinkButton, wz, wzType } from '../wizardStyles';
import ProgressSegments from './ProgressSegments';

const TW = `${TOrbit}.wizard`;

// 6778:17754 / :26219 / :17978: no card; the mark (y 291.4, so 147.4 under the panels' top), a
// centred H1 and its subtitle, the step line, the bar, then the question and tiles, in the 1118
// column the bar spans.
const Entry = styled.section`
  width: 100%;
  max-width: 1118px;
  margin: 0 auto;
  padding-top: 147.4px;
`;

const Hero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const HeroTitle = styled.h1`
  margin: 14.6px 0 0;
  color: ${wz.text};
  font-weight: 700;
  ${wzType.hero}
`;

const HeroSubtitle = styled.p`
  margin: 4px 0 0;
  color: ${wz.muted};
  ${wzType.label}
`;

// The step line sits 10 above the bar; the type picker has none and keeps the same height.
const EntryMeta = styled.div`
  display: flex;
  align-items: flex-end;
  min-height: 46px;
  padding-bottom: 10px;

  > * + * {
    margin-left: 14px;
  }
`;

const StepLabel = styled.span`
  color: ${wz.muted};
  font-weight: 700;
  ${wzType.label}
`;

const Question = styled.h2`
  margin: 0;
  color: ${wz.text};
  font-weight: 700;
  ${wzType.question}
`;

const EntryBody = styled.div`
  margin-top: 30px;

  > ${Question} {
    margin: -9px 0 21px;
  }
`;

// 6778:18418: 1180 wide, r16, `#101830` behind a 0.8 `#212D4B` line, inset 27 / 31.
const Panel = styled.section<{ $narrow: boolean }>`
  width: 100%;
  max-width: ${({ $narrow }) => ($narrow ? '950px' : '1180px')};
  margin: 0 auto;
  padding: ${({ $narrow }) => ($narrow ? '26px 30px 30px' : '27px 31px 31px')};
  min-height: ${({ $narrow }) => ($narrow ? '480px' : 0)};
  border: ${wz.line} solid ${wz.border};
  border-radius: 16px;
  background: ${wz.panel};
`;

const PanelHead = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 31px;
`;

const PanelTitle = styled.h1`
  margin: 0;
  color: ${wz.text};
  font-weight: 700;
  ${wzType.title}
`;

const PanelNav = styled.div`
  display: flex;
  flex: none;
  align-items: center;

  > * + * {
    margin-left: 14px;
  }
`;

const PanelBar = styled.div`
  margin-top: 19.4px;
`;

const PanelQuestion = styled(Question)<{ $afterBar: boolean }>`
  margin-top: ${({ $afterBar }) => ($afterBar ? '17.8px' : '21px')};
`;

const PanelBody = styled.div`
  margin-top: 21.6px;
`;

const PanelFoot = styled.footer<{ $spread: boolean }>`
  display: flex;
  justify-content: ${({ $spread }) => ($spread ? 'space-between' : 'flex-end')};
  margin-top: 24px;
`;

const BackButton: React.FC<{ onBack: () => void; label: string }> = ({ onBack, label }) => (
  <LinkButton type="button" onClick={onBack} data-testid="orbit-wizard-back">
    <LeftOutlined aria-hidden="true" style={{ fontSize: 10 }} />
    <span>{label}</span>
  </LinkButton>
);

interface IWizardEntryProps {
  title: string;
  /** Step number; `null` on the type picker, which lights one segment without a label. */
  step: number | null;
  onBack: (() => void) | null;
  question?: string;
  children: React.ReactNode;
  /** Segments in the bar; the campaign wizard has 5. */
  total?: number;
}

export const WizardEntry: React.FC<IWizardEntryProps> = ({
  title,
  step,
  onBack,
  question,
  children,
  total = 5
}) => {
  const { t } = useMultiLanguageTranslation();
  return (
    <Entry data-testid="orbit-wizard-entry">
      <Hero>
        <OrbitMarkReply width={29} height={30} />
        <HeroTitle>{title}</HeroTitle>
        <HeroSubtitle>{t(`${TW}.subtitle`)}</HeroSubtitle>
      </Hero>
      <EntryMeta>
        {onBack !== null ? <BackButton onBack={onBack} label={t(`${TW}.back`)} /> : null}
        {step !== null ? <StepLabel>{t(`${TW}.step`, { step, total })}</StepLabel> : null}
      </EntryMeta>
      <ProgressSegments lit={step ?? 1} total={total} />
      <EntryBody>
        {question !== undefined ? <Question>{question}</Question> : null}
        {children}
      </EntryBody>
    </Entry>
  );
};

interface IWizardPanelProps {
  title: string;
  /** Step number; `null` on the review, which draws no bar and a lower-case "back". */
  step: number | null;
  onBack: () => void;
  question: string;
  children: React.ReactNode;
  footer: React.ReactNode;
  /** The review's two buttons sit at both ends; a step's single primary sits right. */
  spreadFooter?: boolean;
  testId: string;
  total?: number;
  /** The 950-wide panel of the later frames (6566:9267 delta). */
  narrow?: boolean;
}

export const WizardPanel: React.FC<IWizardPanelProps> = ({
  title,
  step,
  onBack,
  question,
  children,
  footer,
  spreadFooter = false,
  testId,
  total = 5,
  narrow = false
}) => {
  const { t } = useMultiLanguageTranslation();
  return (
    <Panel data-testid={testId} $narrow={narrow}>
      <PanelHead>
        <PanelTitle>{title}</PanelTitle>
        <PanelNav>
          <BackButton
            onBack={onBack}
            label={t(step === null ? `${TW}.reviewBack` : `${TW}.back`)}
          />
          {step !== null ? <StepLabel>{t(`${TW}.step`, { step, total })}</StepLabel> : null}
        </PanelNav>
      </PanelHead>
      {step !== null ? (
        <PanelBar>
          <ProgressSegments lit={step} total={total} />
        </PanelBar>
      ) : null}
      <PanelQuestion $afterBar={step !== null}>{question}</PanelQuestion>
      <PanelBody>{children}</PanelBody>
      <PanelFoot $spread={spreadFooter}>{footer}</PanelFoot>
    </Panel>
  );
};
